export interface ReportFilters {
  startDate?: string;
  endDate?: string;
  viewMode?: string;
  /** Ballot Initiatives report: the cycle the export covers (YYYY-YYYY). */
  initiativeCycle?: string;
  /** Ballot Initiatives report: 'my' (tracked) or 'all' (the cycle's published measures). */
  source?: 'my' | 'all';
}

/** Filters for workspace/org Votes Report (legislator-votes-on-tracked-bills). */
export interface VotesReportFilters extends ReportFilters {
  legislatorParty?: string;
  legislatorChamber?: string;
  billPosition?: string;
  legislatorPosition?: string;
  priorityOnly?: boolean;
  hotBillsOnly?: boolean;
  voteType?: string;
  voteContext?: string;
  hasNotes?: boolean;
  noteAuthor?: string;
  noteTags?: string;
  noteSearch?: string;
}

export interface BillTrackingReport {
  summary: {
    total_bills: number;
    hot_bills: number;
    active_users: number;
    recent_bills: number;
  };
  positionBreakdown: Array<{
    position: string;
    count: number;
    hot_count: number;
  }>;
  trackingTrends: Array<{
    month: string;
    new_bills_count: number;
    position: string;
  }>;
  activeUsers: Array<{
    display_name: string;
    email: string;
    bills_tracked: number;
    hot_bills_tracked: number;
  }>;
}

export interface LegislatorTrackingReport {
  summary: {
    total_legislators: number;
    priority_legislators: number;
    active_users: number;
    recent_tracking: number;
  };
  positionBreakdown: Array<{
    position: string;
    count: number;
    priority_count: number;
  }>;
  partyAnalysis: Array<{
    party_affiliation: string;
    tracking_count: number;
    priority_count: number;
    avg_position_score: number;
  }>;
  topLegislators: Array<{
    full_name: string;
    party_affiliation: string;
    district: string;
    chamber: string;
    tracking_count: number;
    priority_count: number;
    positions: string[];
  }>;
}

export interface TimeTrackingReport {
  summary: {
    total_entries: number;
    completed_entries: number;
    total_seconds: number;
    active_users: number;
    avg_duration_seconds: number;
  };
  userProductivity: Array<{
    display_name: string;
    email: string;
    total_entries: number;
    total_seconds: number;
    avg_duration_seconds: number;
    total_idle_seconds: number;
  }>;
  dailyTrends: Array<{
    date: string;
    entries_count: number;
    total_seconds: number;
    active_users: number;
  }>;
  entryTypes: Array<{
    entry_type: string;
    count: number;
    total_seconds: number;
    avg_duration_seconds: number;
  }>;
}

export interface ActivitySummaryReport {
  summary: {
    total_bills: number;
    total_legislators: number;
    total_time_entries: number;
    total_members: number;
    total_campaigns: number;
  };
  memberActivity: Array<{
    display_name: string;
    email: string;
    role: string;
    joined_at: string;
    bills_tracked: number;
    legislators_tracked: number;
    time_entries: number;
  }>;
  recentActivity: Array<{
    activity_type: string;
    count: number;
  }>;
}

export interface CalendarEvent {
  calendar_id: string;
  reading_title: string;
  calendar_description: string;
  reading_date: string;
  reading_time: string | null;
  room_number: string | null;
  chamber: string;
  committee_flag: boolean;
  upon_adjournment: boolean;
  joint_meeting: boolean;
}

export interface HotBill {
  bill_id: string;
  bill_number: string;
  title: string;
  concerning: string;
  description?: string;
  session: string;
  subject: string | string[];
  summary: string;
  bill_position: string;
  tracked_at: string;
  last_updated: string;
  tracked_by: string;
  tracked_by_email: string;
  latest_action: string;
  latest_location: string;
  latest_date: string;
  calendar_events: CalendarEvent[];
  event_count: number;
  introduced_date?: string;
  latest_amendment_date?: string;
  amendment_count?: number;
  amendments?: Array<{ amendment_number: string; url: string; date: string; status: string }>;
  sponsors?: any[];
  house_committee?: string;
  senate_committee?: string;
  notes?: Array<{
    id: string;
    title: string;
    content: string;
    author: string;
    created_at: string;
    tags?: string[];
  }>;
}

export interface HotBillsReport {
  summary: {
    total_hot_bills: number;
    users_tracking: number;
    upcoming_events: number;
    past_events: number;
    committee_events: number;
    joint_meetings: number;
  };
  hotBills: HotBill[];
}

export type ReportType = 'hot-bills' | 'bill-tracking' | 'legislators' | 'candidates' | 'ballot-initiatives' | 'legislator-votes-on-tracked-bills' | 'scorecard';
export type ExportFormat = 'excel' | 'csv' | 'pdf' | 'word';

/** Standard reports excluded from public share links (subscriber-only).
 *  Keep in sync with backend `NON_PUBLIC_STANDARD_REPORT_TYPES` in
 *  backend/src/constants/publicReportSharing.js (#827). */
export const NON_PUBLIC_STANDARD_REPORT_TYPES = ['legislator-votes-on-tracked-bills'] as const;

export function isShareableStandardReportType(reportType: string): boolean {
  return reportType !== 'sos-report'
    && !NON_PUBLIC_STANDARD_REPORT_TYPES.includes(
      reportType as (typeof NON_PUBLIC_STANDARD_REPORT_TYPES)[number],
    );
}
